import { useQuery } from "@apollo/client";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

import Header from "@/components/header-v2";
import ImmersiveRecap from "@/components/immersive-recap-2023";
import LocationPermission from "@/components/location-permission";
import useAuth from "@/hooks/use-auth";
import useCurrentLocation from "@/hooks/use-current-location";
import useDevice from "@/hooks/use-device";
import useNavBar from "@/hooks/use-nav-bar";
import usePreNavState from "@/hooks/use-pre-nav-state";

import DesktopSearchHeader from "./desktop-search-header";
import { GET_GAMES } from "./graphql";
import useClosedGamesHint from "./hooks/use-closed-games-hint";
import useSearchFilters from "./hooks/use-search-filters";
import useSelectedSortOption from "./hooks/use-selected-sort-option";
import List from "./list";
import ClosedGamesHint from "./list/closed-games-hint";
import MapKit from "./map-kit";
import MobileSearchHeader from "./mobile-search-header";
import type { DataType, VariablesType } from "./types";

export default function Search() {
  const [hoveredObject, setHoveredObject] = useState<string | null>(null);

  const { queryContext } = useAuth();
  const { location } = useCurrentLocation();
  const { isIos } = useDevice();
  useNavBar();
  const [selectedOption] = useSelectedSortOption();
  const search = useSearch({ strict: false }) as ExploreSearchParams;
  const navigate = useNavigate();
  const { filterProps, variables } = useSearchFilters(true);
  const { data, fetchMore, loading } = useQuery<DataType, VariablesType>(
    GET_GAMES,
    queryContext(variables)
  );
  const hasResults = (data?.games?.objects?.length ?? 0) > 0;
  const { shouldShowHint, hasClosedGames, showClosedGames } =
    useClosedGamesHint(
      data?.games?.page ?? 1,
      data ? (data.games.hasNext ?? false) : true,
      hasResults
    );
  const { getStateAndReset } = usePreNavState(
    "scrollPosition",
    () => window.scrollY
  );

  const handleLoadMore = () => {
    if (!loading) {
      fetchMore({
        variables: { ...variables, page: (data?.games?.page ?? 0) + 1 },
      });
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll to top on search change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [search]);

  useEffect(() => {
    if (!search.location && location?.location) {
      navigate({
        replace: true,
        search: (prev: ExploreSearchParams) => ({
          ...prev,
          location: location.location,
        }),
      });
    }
  }, [location, search, navigate]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: its probably not needed
  useEffect(() => {
    if (data) {
      const savedState = getStateAndReset();
      if (savedState) {
        window.scrollTo(0, savedState);
      }
    }
  }, [data]);

  const hasMorePages = useMemo(
    () => (loading ? false : (data?.games.hasNext ?? false)),
    [loading, data]
  );

  const objects = data?.games.objects || [];

  return (
    <>
      <Header
        className="z-10 border-white/10 bg-auto bg-top bg-repeat md:border-b md:bg-auto md:bg-position-[0_450px]"
        isExplorePage
        strategy="fixed"
        style={{
          backgroundColor: "var(--color-midnight-express)",
          backgroundImage: "url(/images/body.png)",
        }}
      >
        <MobileSearchHeader />
      </Header>
      <div className="z-1 pt-16 md:pt-20">
        <div className="flex flex-col justify-between md:flex-row">
          <div className="w-full bg-[url(/images/body.png)] bg-auto bg-position-[0_370px] shadow-2-0-5px shadow-black/31 md:z-5 md:w-1/2">
            <DesktopSearchHeader filterProps={filterProps} />
            <List
              dataLength={objects.length}
              hasClosedGames={hasClosedGames}
              hasMore={hasMorePages}
              isIos={isIos}
              loader={null}
              loading={!objects.length && loading}
              next={handleLoadMore}
              objects={objects}
              onHover={setHoveredObject}
              onShowClosedGames={showClosedGames}
              sortBy={selectedOption}
            />
          </div>
          <MapKit
            hasListItems={!!objects.length || loading}
            hoveredObject={hoveredObject}
            isIos={isIos}
            variables={variables}
          />
        </div>
      </div>
      <ImmersiveRecap
        className="absolute top-18 left-0 w-full px-4"
        withCloseButton
      />
      <LocationPermission />
      <ClosedGamesHint
        onShowClosedGames={showClosedGames}
        shouldShowHint={shouldShowHint}
      />
    </>
  );
}
